import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

const MOIS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre']

const CATEGORIES = {
  'Recettes': ['Honoraires - CB', 'Honoraires - chèque', 'Honoraires - virement', 'Autres recettes'],
  'Charges cabinet': ['Loyer cabinet', 'Charges communes cabinet', 'Charges mensuelles', 'Frais bancaires / TPE', 'Location TPE', 'Remboursement prêt - capital', 'Remboursement prêt - intérêts', 'Remboursement prêt - assurance', 'Formation professionnelle', 'Fournitures cabinet', 'Matériel', 'Abonnement logiciel', 'Frais divers cabinet'],
  'Cotisations / Prévoyance': ['Cotisations URSSAF', 'Retraite complémentaire (Madelin)', 'Prévoyance (Madelin)', 'Prévoyance santé (Madelin)', 'Autres cotisations'],
  'Charges diverses pro': ['Téléphone cabinet', 'Abonnement musique', 'Transport professionnel', 'Repas professionnel', 'Frais divers pro'],
  'Dépense personnelle': ['Chèque personnel', 'Achat personnel', 'Autre dépense personnelle'],
  'Prélèvement exploitant': ['Rémunération exploitant'],
  'Perso': ['Dépense personnelle'],
  'À vérifier': ['À catégoriser'],
}

const NATURE_MAP = {
  'Recettes': 'recette',
  'Charges cabinet': 'deductible',
  'Cotisations / Prévoyance': 'deductible',
  'Charges diverses pro': 'deductible',
  'Dépense personnelle': 'non_deductible',
  'Prélèvement exploitant': 'non_deductible',
  'Perso': 'non_deductible',
  'À vérifier': 'a_verifier',
}

// Catégorisation automatique par mots-clés
const KEYWORDS = [
  { pattern: /URSSAF/i, cat: 'Cotisations / Prévoyance', sous: 'Cotisations URSSAF', nature: 'deductible' },
  { pattern: /AGIPI.*RETRAITE/i, cat: 'Cotisations / Prévoyance', sous: 'Retraite complémentaire (Madelin)', nature: 'deductible' },
  { pattern: /AGIPI.*PREVOYANCE|AGIPI-ADIS/i, cat: 'Cotisations / Prévoyance', sous: 'Prévoyance (Madelin)', nature: 'deductible' },
  { pattern: /SWISSLIFE/i, cat: 'Cotisations / Prévoyance', sous: 'Prévoyance santé (Madelin)', nature: 'deductible' },
  { pattern: /LOYER/i, cat: 'Charges cabinet', sous: 'Loyer cabinet', nature: 'deductible' },
  { pattern: /CHARGES.MENSUELLES/i, cat: 'Charges cabinet', sous: 'Charges mensuelles', nature: 'deductible' },
  { pattern: /CHARGES.COMMUNES/i, cat: 'Charges cabinet', sous: 'Charges communes cabinet', nature: 'deductible' },
  { pattern: /FORFAIT.COM|FACT.SGT/i, cat: 'Charges cabinet', sous: 'Frais bancaires / TPE', nature: 'deductible' },
  { pattern: /EURO.INFORMATION|PREL.*TPE/i, cat: 'Charges cabinet', sous: 'Location TPE', nature: 'deductible' },
  { pattern: /ECH.PRET.*CAP/i, cat: 'Charges cabinet', sous: 'Remboursement prêt - capital', nature: 'non_deductible' },
  { pattern: /ECH.PRET.*INT/i, cat: 'Charges cabinet', sous: 'Remboursement prêt - intérêts', nature: 'deductible' },
  { pattern: /ECH.PRET.*ASS/i, cat: 'Charges cabinet', sous: 'Remboursement prêt - assurance', nature: 'deductible' },
  { pattern: /REMCB|TPE\d+/i, cat: 'Recettes', sous: 'Honoraires - CB', nature: 'recette' },
  { pattern: /REM.CHQ/i, cat: 'Recettes', sous: 'Honoraires - chèque', nature: 'recette' },
  { pattern: /VRST.REF|VIR.*INST(?!.*SALAIRE)/i, cat: 'Recettes', sous: 'Honoraires - virement', nature: 'recette' },
  { pattern: /VIREMENT.SALAIRE|VIR.*SALAIRE/i, cat: 'Prélèvement exploitant', sous: 'Rémunération exploitant', nature: 'non_deductible' },
  { pattern: /FREE.MOBILE/i, cat: 'Charges diverses pro', sous: 'Téléphone cabinet', nature: 'deductible' },
  { pattern: /AMAZON.MUSIC/i, cat: 'Charges diverses pro', sous: 'Abonnement musique', nature: 'deductible' },
  { pattern: /ARRHES|THEORIE|FORMATION/i, cat: 'Charges cabinet', sous: 'Formation professionnelle', nature: 'deductible' },
]

function autoCateg(libelle, memo) {
  // D'abord vérifier la mémoire personnalisée
  if (memo) {
    const found = memo.find(m => libelle.toUpperCase().includes(m.libelle_pattern.toUpperCase()))
    if (found) return { categorie: found.categorie, sous_categorie: found.sous_categorie, nature: found.nature }
  }
  // Puis les mots-clés par défaut
  for (const kw of KEYWORDS) {
    if (kw.pattern.test(libelle)) {
      return { categorie: kw.cat, sous_categorie: kw.sous, nature: kw.nature }
    }
  }
  return { categorie: 'À vérifier', sous_categorie: 'À catégoriser', nature: 'a_verifier' }
}

const CAT_COLORS = {
  'Recettes': { bg: '#E8F5E9', border: '#2E7D32', text: '#1B5E20' },
  'Charges cabinet': { bg: '#FFEBEE', border: '#C62828', text: '#B71C1C' },
  'Cotisations / Prévoyance': { bg: '#FFF3E0', border: '#E65100', text: '#BF360C' },
  'Charges diverses pro': { bg: '#FFF8E1', border: '#F57F17', text: '#E65100' },
  'Prélèvement exploitant': { bg: '#F3E5F5', border: '#6A1B9A', text: '#4A148C' },
  'Dépense personnelle': { bg: '#E8EAF6', border: '#3949AB', text: '#1A237E' },
  'À vérifier': { bg: '#FFF9C4', border: '#F9A825', text: '#F57F17' },
}

export default function RelevesBancaires() {
  const { praticien } = useAuth()
  const [searchParams] = useSearchParams()
  const [releves, setReleves] = useState([])
  const [releveActif, setReleveActif] = useState(null)
  const [operations, setOperations] = useState([])
  const [memo, setMemo] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [panelNew, setPanelNew] = useState(false)
  const [moisForm, setMoisForm] = useState(new Date().getMonth() + 1)
  const [anneeForm, setAnneeForm] = useState(new Date().getFullYear())
  const [operationEditing, setOperationEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [filterCat, setFilterCat] = useState('all')
  const [entite, setEntite] = useState(() => searchParams.get('entite') || 'bnc')
  const fileInputRef = useRef()
  const justifInputRef = useRef()
  const justifOpId = useRef(null)
  const [justificatifs, setJustificatifs] = useState({})

  // Sync entite avec URL
  useEffect(() => {
    const e = searchParams.get('entite') || 'bnc'
    setEntite(e)
    setReleveActif(null)
    setOperations([])
  }, [searchParams])

  const loadReleves = useCallback(async () => {
    if (!praticien?.id) return
    let query = supabase.from('releves_bancaires').select('*')
    if (entite === 'bnc') {
      query = query.eq('praticien_id', praticien.id).eq('entite', 'bnc')
    } else {
      query = query.eq('entite', entite).eq('partage', true)
    }
    const { data } = await query.order('annee', { ascending: false }).order('mois', { ascending: false })
    setReleves(data || [])
  }, [praticien?.id, entite])

  const loadMemo = useCallback(async () => {
    if (!praticien?.id) return
    const { data } = await supabase.from('releve_categories_memo')
      .select('*').eq('praticien_id', praticien.id)
    setMemo(data || [])
  }, [praticien?.id])

  useEffect(() => { loadReleves(); loadMemo() }, [loadReleves, loadMemo])

  async function getSignedUrl(path) {
    if (!path) return null
    const { data, error } = await supabase.storage.from('releves-bancaires').createSignedUrl(path, 3600)
    if (error || !data) return null
    return data.signedUrl
  }

  async function loadOperations(releveId) {
    setLoading(true)
    const { data } = await supabase.from('releve_operations')
      .select('*').eq('releve_id', releveId)
      .order('date_operation')
    setOperations(data || [])
    // Charger les justificatifs pour toutes les opérations
    if (data && data.length > 0) {
      const opIds = data.map(op => op.id)
      const { data: justifs } = await supabase.from('releve_justificatifs')
        .select('*').in('operation_id', opIds)
      const byOp = {}
      for (const j of (justifs || [])) {
        if (!byOp[j.operation_id]) byOp[j.operation_id] = []
        byOp[j.operation_id].push(j)
      }
      setJustificatifs(byOp)
    } else {
      setJustificatifs({})
    }
    setLoading(false)
  }

  async function selectReleve(r) {
    setReleveActif(r)
    setFilterCat('all')
    await loadOperations(r.id)
  }

  // Upload + parsing du PDF
  async function handleUploadReleve(e) {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true)
    try {
      // Upload dans Supabase Storage
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const path = `${praticien.id}/${anneeForm}-${String(moisForm).padStart(2,'0')}/${safeName}`
      const { error: upErr } = await supabase.storage.from('releves-bancaires').upload(path, file, { upsert: true })
      if (upErr) { alert('Erreur upload : ' + upErr.message); setUploading(false); return }

      // Créer le relevé en base
      const { data: releve, error } = await supabase.from('releves_bancaires').insert({
        praticien_id: praticien.id,
        mois: moisForm,
        annee: anneeForm,
        fichier_nom: safeName,
        fichier_path: path,
        entite: entite,
        partage: entite !== 'bnc',
        statut: 'en_cours'
      }).select().single()
      if (error) { alert('Erreur : ' + error.message); setUploading(false); return }

      // Lire le PDF et extraire les opérations
      const reader = new FileReader()
      reader.onload = async (ev) => {
        const text = await extractTextFromPDF(ev.target.result)
        console.log('PDF text extrait (500 premiers chars):', text.slice(0, 500))
        const ops = parseBankStatement(text)
        console.log('Opérations parsées:', ops.length, ops.slice(0, 3))
        if (ops.length > 0) {
          const toInsert = ops.map(op => ({
            releve_id: releve.id,
            praticien_id: praticien.id,
            date_operation: op.date,
            libelle: op.libelle,
            debit: op.debit || null,
            credit: op.credit || null,
            ...autoCateg(op.libelle, memo),
            valide: false,
          }))
          console.log('Premier enregistrement à insérer:', JSON.stringify(toInsert[0]))
          const { error: insErr } = await supabase.from('releve_operations').insert(toInsert)
          if (insErr) console.error('Erreur insert:', insErr.message, insErr.details, insErr.hint)
          // Calculer totaux
          const totalDebit = ops.reduce((s, o) => s + (o.debit || 0), 0)
          const totalCredit = ops.reduce((s, o) => s + (o.credit || 0), 0)
          await supabase.from('releves_bancaires').update({
            total_debit: totalDebit, total_credit: totalCredit
          }).eq('id', releve.id)
        }
        setPanelNew(false)
        await loadReleves()
        await selectReleve({ ...releve, total_debit: ops.reduce((s,o) => s+(o.debit||0),0), total_credit: ops.reduce((s,o) => s+(o.credit||0),0) })
        setUploading(false)
      }
      reader.readAsArrayBuffer(file)
    } catch(e) {
      alert('Erreur : ' + e.message)
      setUploading(false)
    }
  }

  async function extractTextFromPDF(arrayBuffer) {
    try {
      const pdfjsLib = await import('pdfjs-dist/build/pdf.mjs')
      pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.js`
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer, useWorkerFetch: false, isEvalSupported: false, useSystemFonts: true }).promise
      let text = ''
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const content = await page.getTextContent()
        const pageText = content.items.map(item => item.str).join(' ')
        text += pageText + '\n'
      }
      return text
    } catch(e) {
      console.error('PDF parse error:', e)
      return ''
    }
  }

  // Parser le texte du relevé Crédit Mutuel
  function parseBankStatement(text) {
    const ops = []
    const cleaned = text.replace(/\s+/g, ' ')
    const datePattern = /(\d{2}\/\d{2}\/\d{4})\s+(\d{2}\/\d{2}\/\d{4})\s+/g
    const matches = []
    let m
    while ((m = datePattern.exec(cleaned)) !== null) {
      matches.push({ index: m.index, date: m[1], end: m.index + m[0].length })
    }
    for (let i = 0; i < matches.length; i++) {
      const start = matches[i].end
      const end = i + 1 < matches.length ? matches[i + 1].index : cleaned.length
      // Supprimer les numéros de compte Crédit Mutuel avant de parser
      let segment = cleaned.slice(start, end).trim()
      segment = segment.replace(/\b0\d{4}\s+\d{6}\s+\d{2}\b/g, '')
      segment = segment.replace(/\b\d{5}\s+\d{6}\b/g, '')
      // Supprimer les lignes de totaux et solde (faussent le montant de la dernière opération)
      segment = segment.replace(/Total des mouvements[\s\d,\.]+/gi, '')
      segment = segment.replace(/SOLDE\s+CREDITEUR[^€\d]*[\d\s,\.]+/gi, '')
      segment = segment.replace(/SOLDE\s+DEBITEUR[^€\d]*[\d\s,\.]+/gi, '')
      segment = segment.replace(/Réf\s*:\s*\d+[^€]*/gi, '')
      segment = segment.replace(/\s+/g, ' ').trim()
      const amountRegex = /(?<!\d)(\d{1,4},\d{2})(?!\d)/g
      const amounts = []
      let am
      while ((am = amountRegex.exec(segment)) !== null) {
        const val = parseFloat(am[1].replace(',', '.'))
        if (val >= 1 && val <= 9999.99) {
          amounts.push({ value: val, index: am.index })
        }
      }
      if (amounts.length === 0) continue
      const montantObj = amounts[amounts.length - 1]
      const libelle = segment.slice(0, montantObj.index).trim().replace(/\s+/g, ' ').slice(0, 80)
      if (!libelle || libelle.length < 3) continue
      const lib = libelle.toUpperCase()
      let debit = null, credit = null
      // Virements vers soi-même (WHITELAW, COUDERT) = débit
      const isVirementSoi = /WHITELAW|COUDERT|VIR.*MME.*WHITEL|VIR.*MG.*COUD|VIR.*M\.G\..*COUD/.test(lib)
      // Recettes : remises CB, chèques, virements reçus (pas vers soi)
      const isCredit = !isVirementSoi && /REMCB|REM\s*CHQ|VRST\s*REF/.test(lib)
      // Pour CB/PRLV : prendre premier montant (évite de prendre le solde)
      const isCB = /^CB\s|^PRLV\s|^PRELEVEMENT/.test(lib)
      const montantChoisi = (isCB && amounts.length > 1) ? amounts[0] : amounts[amounts.length - 1]
      if (!montantChoisi) continue
      if (isCredit) credit = montantChoisi.value
      else debit = montantChoisi.value
      const [d, mo, y] = matches[i].date.split('/')
      ops.push({ date: `${y}-${mo}-${d}`, libelle, debit, credit })
    }
    return ops
  }

  // Saisie manuelle d'une opération
  async function saveOperation(op) {
    setSaving(true)
    await supabase.from('releve_operations').update({
      categorie: op.categorie,
      sous_categorie: op.sous_categorie,
      nature: op.nature,
      notes: op.notes,
      debit: op.debit,
      credit: op.credit,
      valide: true,
    }).eq('id', op.id)

    // Mémoriser la catégorie pour les prochaines fois
    if (op.libelle && op.categorie !== 'À vérifier') {
      const pattern = op.libelle.split(' ').slice(0, 3).join(' ').toUpperCase()
      await supabase.from('releve_categories_memo').upsert({
        praticien_id: praticien.id,
        libelle_pattern: pattern,
        categorie: op.categorie,
        sous_categorie: op.sous_categorie,
        nature: op.nature,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'praticien_id,libelle_pattern' })
      await loadMemo()
    }
    setSaving(false)
    setOperationEditing(null)
    await loadOperations(releveActif.id)
    // Recalculer les totaux du relevé
    const { data: ops } = await supabase.from('releve_operations').select('debit,credit').eq('releve_id', releveActif.id)
    if (ops) {
      const totalDebit = ops.reduce((s, o) => s + (o.debit || 0), 0)
      const totalCredit = ops.reduce((s, o) => s + (o.credit || 0), 0)
      await supabase.from('releves_bancaires').update({ total_debit: totalDebit, total_credit: totalCredit }).eq('id', releveActif.id)
    }
  }

  // Upload justificatif pour une opération
  async function handleJustifUpload(e) {
    const file = e.target.files[0]
    if (!file || !justifOpId.current) return
    const safeJustifName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
    const path = `${praticien.id}/justifs/${justifOpId.current}/${safeJustifName}`
    const { error } = await supabase.storage.from('releves-bancaires').upload(path, file, { upsert: false })
    if (error && error.message !== 'The resource already exists') { alert('Erreur upload justif : ' + error.message); return }
    await supabase.from('releve_justificatifs').insert({
      operation_id: justifOpId.current,
      fichier_nom: safeJustifName,
      fichier_path: path
    })
    e.target.value = ''
    await loadOperations(releveActif.id)
  }

  // Export ZIP
  async function exportZip(avecJustifs) {
    setExporting(true)
    try {
      const JSZip = (await import('jszip')).default
      const zip = new JSZip()

      // Générer le CSV des opérations
      const header = 'Date;Libellé;Catégorie;Sous-catégorie;Nature;Débit;Crédit;Notes;Justificatif\n'
      const rows = operations.map(op =>
        `${op.date_operation};${op.libelle};${op.categorie || ''};${op.sous_categorie || ''};${op.nature || ''};${op.debit || ''};${op.credit || ''};${op.notes || ''};${op.justificatif_nom || ''}`
      ).join('\n')
      zip.file(`releve_${releveActif.annee}_${String(releveActif.mois).padStart(2,'0')}.csv`, header + rows)

      // Ajouter le PDF du relevé
      if (releveActif.fichier_path) {
        try {
          const signedUrl = await getSignedUrl(releveActif.fichier_path)
          if (signedUrl) {
            const res = await fetch(signedUrl)
            const blob = await res.blob()
            zip.file(releveActif.fichier_nom || 'releve.pdf', blob)
          }
        } catch(e) { console.warn('PDF non récupéré', e) }
      }

      // Ajouter les justificatifs
      if (avecJustifs) {
        const folder = zip.folder('justificatifs')
        for (const op of operations) {
          const opJustifs = justificatifs[op.id] || []
          for (const j of opJustifs) {
            try {
              const signedUrl = await getSignedUrl(j.fichier_path)
              if (signedUrl) {
                const res = await fetch(signedUrl)
                const blob = await res.blob()
                const prefix = op.date_operation ? op.date_operation.slice(0,10) + '_' : ''
                folder.file(prefix + j.fichier_nom, blob)
              }
            } catch(e) { console.warn('Justif non récupéré', e) }
          }
        }
      }

      const content = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(content)
      const a = document.createElement('a')
      a.href = url
      a.download = `comptabilite_${MOIS[releveActif.mois-1]}_${releveActif.annee}.zip`
      a.click()
      URL.revokeObjectURL(url)
    } catch(e) {
      alert('Erreur export : ' + e.message)
    }
    setExporting(false)
  }

  const opsFiltered = filterCat === 'all' ? operations
    : filterCat === 'À vérifier' ? operations.filter(o => o.debit && !(justificatifs[o.id]?.length) && !o.sans_justificatif)
    : filterCat === 'Traités sans catégorie' ? operations.filter(o => o.debit && (justificatifs[o.id]?.length > 0 || o.sans_justificatif) && (!o.categorie || o.categorie === 'À vérifier'))
    : operations.filter(o => o.categorie === filterCat)
  const totalCredit = operations.filter(o => o.credit).reduce((s, o) => s + o.credit, 0)
  const totalDebit = operations.filter(o => o.debit).reduce((s, o) => s + o.debit, 0)
  const nonValides = operations.filter(o => o.debit && !(justificatifs[o.id]?.length) && !o.sans_justificatif).length

  return (
    <>
      <div style={{display:'flex', gap:16, height:'calc(100vh - 120px)', overflow:'hidden'}}>

        {/* Sidebar relevés */}
        <div style={{width:220, flexShrink:0, display:'flex', flexDirection:'column', gap:8}}>
          <button className="btn btn-sm" style={{background:'#1D9E75', color:'#fff', border:'none', fontWeight:600, justifyContent:'center'}}
            onClick={() => setPanelNew(true)}>
            + Nouveau relevé
          </button>
          {releves.length === 0 && <div style={{fontSize:12, color:'var(--text-3)', padding:'8px 4px'}}>Aucun relevé</div>}
          {releves.map(r => (
            <div key={r.id}
              onClick={() => selectReleve(r)}
              style={{padding:'10px 12px', borderRadius:'var(--radius)', cursor:'pointer', border: releveActif?.id === r.id ? '2px solid #1D9E75' : '1px solid var(--border)', background: releveActif?.id === r.id ? '#E8F5E9' : 'var(--surface)', transition:'all .1s'}}>
              <div style={{fontSize:13, fontWeight:600, color: releveActif?.id === r.id ? '#1B5E20' : 'var(--text)'}}>
                {MOIS[r.mois-1]} {r.annee}
              </div>
              <div style={{fontSize:11, color:'var(--text-3)', marginTop:2}}>
                {r.statut === 'envoye' ? '✓ Envoyé' : r.statut === 'valide' ? '✓ Validé' : '● En cours'}
              </div>
              {r.total_credit != null && (
                <div style={{fontSize:11, color:'#2E7D32', marginTop:2}}>+{r.total_credit?.toFixed(0)} €</div>
              )}
            </div>
          ))}
        </div>

        {/* Zone principale */}
        <div style={{flex:1, overflow:'auto', display:'flex', flexDirection:'column', gap:12}}>
          {!releveActif ? (
            <div className="empty" style={{marginTop:60}}>Sélectionnez un relevé ou importez-en un nouveau</div>
          ) : (
            <>
              {/* En-tête relevé */}
              <div style={{display:'flex', alignItems:'center', gap:16, flexWrap:'wrap'}}>
                <div>
                  <div style={{fontSize:16, fontWeight:700}}>{MOIS[releveActif.mois-1]} {releveActif.annee}</div>
                  <div style={{fontSize:12, color:'var(--text-3)'}}>{releveActif.fichier_nom}</div>
                </div>
                <div className="stats-grid" style={{flex:1, gap:8, marginBottom:0}}>
                  <div className="stat-card" style={{padding:'8px 14px'}}>
                    <div className="label">Recettes</div>
                    <div className="value" style={{color:'#2E7D32', fontSize:16}}>+{totalCredit.toFixed(0)} €</div>
                  </div>
                  <div className="stat-card" style={{padding:'8px 14px'}}>
                    <div className="label">Dépenses</div>
                    <div className="value" style={{color:'#C62828', fontSize:16}}>{totalDebit.toFixed(0)} €</div>
                  </div>
                  <div className="stat-card" style={{padding:'8px 14px'}}>
                    <div className="label">À vérifier</div>
                    <div className="value" style={{color: nonValides > 0 ? '#F57F17' : '#2E7D32', fontSize:16}}>{nonValides}</div>
                  </div>
                </div>
                <div style={{display:'flex', gap:8}}>
                  <button className="btn btn-sm" onClick={() => exportZip(false)} disabled={exporting}>
                    {exporting ? '…' : '↓ ZIP (relevé + CSV)'}
                  </button>
                  <button className="btn btn-sm" style={{background:'#185FA5', color:'#fff', border:'none'}}
                    onClick={() => exportZip(true)} disabled={exporting}>
                    {exporting ? '…' : '↓ ZIP + justificatifs'}
                  </button>
                </div>
              </div>

              {/* Filtre catégorie */}
              <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                {['all', 'À vérifier', 'Traités sans catégorie', ...Object.keys(CATEGORIES).filter(c => c !== 'À vérifier')].map(cat => {
                  const count = cat === 'all' ? operations.length
                    : cat === 'À vérifier' ? operations.filter(o => o.debit && !(justificatifs[o.id]?.length) && !o.sans_justificatif).length
                    : cat === 'Traités sans catégorie' ? operations.filter(o => o.debit && (justificatifs[o.id]?.length > 0 || o.sans_justificatif) && (!o.categorie || o.categorie === 'À vérifier')).length
                    : operations.filter(o => o.categorie === cat).length
                  if (cat !== 'all' && count === 0) return null
                  const col = CAT_COLORS[cat]
                  return (
                    <button key={cat}
                      onClick={() => setFilterCat(cat)}
                      style={{padding:'4px 10px', borderRadius:12, fontSize:11, fontWeight:500, cursor:'pointer',
                        border: filterCat === cat ? '2px solid ' + (col?.border || '#333') : '1px solid var(--border)',
                        background: filterCat === cat ? (col?.bg || '#f0f0f0') : 'transparent',
                        color: filterCat === cat ? (col?.text || '#333') : 'var(--text-2)'}}>
                      {cat === 'all' ? 'Tout' : cat} ({count})
                    </button>
                  )
                })}
              </div>

              {/* Liste opérations */}
              {loading ? <div className="empty">Chargement…</div> : (
                <div style={{display:'flex', flexDirection:'column', gap:4}}>
                  {opsFiltered.length === 0 && (
                    <div className="empty" style={{padding:40}}>
                      Aucune opération — importez un relevé PDF pour commencer
                      <div style={{marginTop:12}}>
                        <button className="btn btn-sm" style={{background:'#1D9E75', color:'#fff', border:'none'}}
                          onClick={() => { justifOpId.current = null; fileInputRef.current?.click() }}>
                          Saisir les opérations manuellement
                        </button>
                      </div>
                    </div>
                  )}
                  {opsFiltered.map(op => {
                    const col = CAT_COLORS[op.categorie] || CAT_COLORS['À vérifier']
                    const isEditing = operationEditing?.id === op.id
                    return (
                      <div key={op.id} style={{border: '1px solid ' + (op.valide ? col.border : '#F9A825'), borderRadius:'var(--radius)', background: col.bg, overflow:'hidden'}}>
                        {/* Ligne principale */}
                        <div style={{display:'flex', alignItems:'center', gap:10, padding:'8px 12px'}}>
                          <div style={{width:80, fontSize:12, color:'var(--text-3)', flexShrink:0}}>
                            {op.date_operation}
                          </div>
                          <div style={{flex:1, minWidth:0}}>
                            <div style={{fontSize:12, fontWeight:500, color:'var(--text)', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis'}}>
                              {op.libelle}
                            </div>
                            <div style={{fontSize:11, color: col.text, marginTop:1}}>
                              {op.categorie} {op.sous_categorie ? '· ' + op.sous_categorie : ''}
                            </div>
                          </div>
                          <div style={{textAlign:'right', flexShrink:0}}>
                            {op.credit && <div style={{fontSize:13, fontWeight:700, color:'#2E7D32'}}>+{op.credit.toFixed(2)} €</div>}
                            {op.debit && <div style={{fontSize:13, fontWeight:700, color:'#C62828'}}>{op.debit.toFixed(2)} €</div>}
                          </div>
                          <div style={{display:'flex', gap:4, flexShrink:0, alignItems:'center', flexWrap:'wrap'}}>
                            {/* Badge justificatifs */}
                            {op.sans_justificatif ? (
                              <button className="btn btn-sm"
                                style={{fontSize:10, padding:'3px 8px', background:'#F5F5F5', border:'1px solid #DDD', color:'#888'}}
                                title="Marqué sans justificatif — cliquer pour annuler"
                                onClick={async () => {
                                  await supabase.from('releve_operations').update({ sans_justificatif: false }).eq('id', op.id)
                                  await loadOperations(releveActif.id)
                                }}>
                                — sans justif
                              </button>
                            ) : (justificatifs[op.id] || []).length > 0 ? (
                              <div style={{position:'relative'}}>
                                <button className="btn btn-sm"
                                  style={{fontSize:11, padding:'3px 8px', background:'#E8F5E9', border:'1px solid #A5D6A7', color:'#2E7D32', fontWeight:700}}
                                  onClick={() => setOperationEditing(operationEditing?.id === op.id && operationEditing?._showJustifs ? null : {...op, _showJustifs: true})}>
                                  ✓ {(justificatifs[op.id] || []).length}
                                </button>
                                {isEditing && operationEditing?._showJustifs && (
                                  <div style={{position:'absolute', right:0, top:'110%', background:'white', border:'1px solid #DDD', borderRadius:6, boxShadow:'0 4px 12px rgba(0,0,0,.12)', zIndex:100, minWidth:220, padding:8}}>
                                    {(justificatifs[op.id] || []).map(j => (
                                      <div key={j.id} style={{display:'flex', alignItems:'center', gap:6, padding:'4px 0', borderBottom:'1px solid #F0F0F0'}}>
                                        <span style={{fontSize:11, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{j.fichier_nom}</span>
                                        <button style={{fontSize:11, background:'none', border:'none', color:'#1D9E75', cursor:'pointer', padding:'0 4px'}}
                                          onClick={async () => { const url = await getSignedUrl(j.fichier_path); if (url) window.open(url, '_blank') }}>
                                          👁
                                        </button>
                                        <button style={{fontSize:11, background:'none', border:'none', color:'#C62828', cursor:'pointer', padding:'0 4px'}}
                                          onClick={async () => {
                                            await supabase.from('releve_justificatifs').delete().eq('id', j.id)
                                            await loadOperations(releveActif.id)
                                          }}>
                                          ✕
                                        </button>
                                      </div>
                                    ))}
                                    <button className="btn btn-sm" style={{marginTop:6, width:'100%', fontSize:11}}
                                      onClick={() => { justifOpId.current = op.id; justifInputRef.current?.click() }}>
                                      + Ajouter un fichier
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div style={{display:'flex', gap:4}}>
                                <button className="btn btn-sm" style={{fontSize:10, padding:'3px 8px', opacity:.6}} title="Ajouter un justificatif"
                                  onClick={() => { justifOpId.current = op.id; justifInputRef.current?.click() }}>
                                  📎
                                </button>
                                <button className="btn btn-sm" style={{fontSize:10, padding:'3px 6px', opacity:.5}} title="Pas de justificatif pour cette opération"
                                  onClick={async () => {
                                    await supabase.from('releve_operations').update({ sans_justificatif: true }).eq('id', op.id)
                                    await loadOperations(releveActif.id)
                                  }}>
                                  —
                                </button>
                              </div>
                            )}
                            <button className="btn btn-sm" style={{fontSize:10, padding:'3px 8px'}}
                              onClick={() => setOperationEditing(isEditing ? null : { ...op })}>
                              {isEditing ? '✕' : '✎'}
                            </button>
                          </div>
                        </div>

                        {/* Formulaire d'édition inline */}
                        {isEditing && (
                          <div style={{padding:'10px 12px', borderTop:'1px solid ' + col.border, background:'rgba(255,255,255,.7)'}}>
                            <div style={{display:'flex', gap:10, flexWrap:'wrap', marginBottom:8}}>
                              <div className="form-group" style={{marginBottom:0, flex:'0 0 120px'}}>
                                <label style={{fontSize:11}}>Débit (€)</label>
                                <input type="number" step="0.01" style={{fontSize:12}}
                                  value={operationEditing.debit || ''}
                                  onChange={e => setOperationEditing({...operationEditing, debit: e.target.value ? parseFloat(e.target.value) : null, credit: e.target.value ? null : operationEditing.credit})}
                                  placeholder="0.00" />
                              </div>
                              <div className="form-group" style={{marginBottom:0, flex:'0 0 120px'}}>
                                <label style={{fontSize:11}}>Crédit (€)</label>
                                <input type="number" step="0.01" style={{fontSize:12}}
                                  value={operationEditing.credit || ''}
                                  onChange={e => setOperationEditing({...operationEditing, credit: e.target.value ? parseFloat(e.target.value) : null, debit: e.target.value ? null : operationEditing.debit})}
                                  placeholder="0.00" />
                              </div>
                            </div>
                            <div style={{display:'flex', gap:10, flexWrap:'wrap', marginBottom:8}}>
                              <div className="form-group" style={{marginBottom:0, flex:1, minWidth:160}}>
                                <label style={{fontSize:11}}>Catégorie</label>
                                <select style={{fontSize:12}} value={operationEditing.categorie || ''} onChange={e => {
                                  const cat = e.target.value
                                  const sousCats = CATEGORIES[cat] || []
                                  setOperationEditing({...operationEditing, categorie: cat, sous_categorie: sousCats[0] || '', nature: NATURE_MAP[cat] || 'a_verifier'})
                                }}>
                                  {Object.keys(CATEGORIES).map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </div>
                              <div className="form-group" style={{marginBottom:0, flex:1, minWidth:160}}>
                                <label style={{fontSize:11}}>Sous-catégorie</label>
                                <select style={{fontSize:12}} value={operationEditing.sous_categorie || ''} onChange={e => setOperationEditing({...operationEditing, sous_categorie: e.target.value})}>
                                  {(CATEGORIES[operationEditing.categorie] || []).map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                              </div>
                              <div className="form-group" style={{marginBottom:0, flex:1, minWidth:160}}>
                                <label style={{fontSize:11}}>Nature fiscale</label>
                                <select style={{fontSize:12}} value={operationEditing.nature || ''} onChange={e => setOperationEditing({...operationEditing, nature: e.target.value})}>
                                  <option value="recette">Recette</option>
                                  <option value="deductible">Charge déductible</option>
                                  <option value="non_deductible">Non déductible</option>
                                  <option value="a_verifier">À vérifier</option>
                                </select>
                              </div>
                            </div>
                            <div className="form-group" style={{marginBottom:8}}>
                              <label style={{fontSize:11}}>Notes</label>
                              <input style={{fontSize:12}} value={operationEditing.notes || ''} onChange={e => setOperationEditing({...operationEditing, notes: e.target.value})} placeholder="Précisions pour le comptable…" />
                            </div>
                            <div style={{display:'flex', gap:8, alignItems:'center'}}>
                              <button className="btn btn-primary btn-sm" onClick={() => saveOperation(operationEditing)} disabled={saving}>
                                {saving ? 'Sauvegarde…' : '✓ Valider et mémoriser'}
                              </button>
                              <span style={{fontSize:11, color:'var(--text-3)'}}>
                                La catégorie sera mémorisée pour les prochains relevés
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Input fichiers cachés */}
      <input ref={justifInputRef} type="file" accept="image/*,.pdf" style={{display:'none'}} onChange={handleJustifUpload} />

      {/* Panel nouveau relevé */}
      {panelNew && (
        <>
          <div className="panel-overlay" onClick={() => setPanelNew(false)} />
          <div className="panel" style={{width:440}}>
            <div className="panel-header">
              <h3>Importer un relevé bancaire</h3>
              <button className="close-btn" onClick={() => setPanelNew(false)}>✕</button>
            </div>
            <div className="panel-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Mois</label>
                  <select value={moisForm} onChange={e => setMoisForm(parseInt(e.target.value))}>
                    {MOIS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Année</label>
                  <select value={anneeForm} onChange={e => setAnneeForm(parseInt(e.target.value))}>
                    {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <div style={{border:'2px dashed var(--border-md)', borderRadius:'var(--radius)', padding:32, textAlign:'center', marginTop:8}}>
                <div style={{fontSize:32, marginBottom:8}}>📄</div>
                <div style={{fontSize:14, fontWeight:500, marginBottom:4}}>Relevé PDF Crédit Mutuel</div>
                <div style={{fontSize:12, color:'var(--text-3)', marginBottom:16}}>
                  Le relevé sera analysé automatiquement et les opérations extraites
                </div>
                <label style={{cursor:'pointer'}}>
                  <span className="btn btn-primary" style={{background:'#1D9E75', border:'none'}}>
                    {uploading ? 'Analyse en cours…' : 'Sélectionner le PDF'}
                  </span>
                  <input type="file" accept=".pdf" style={{display:'none'}} onChange={handleUploadReleve} disabled={uploading} />
                </label>
              </div>
              <div style={{marginTop:16, padding:'10px 14px', background:'var(--bg)', borderRadius:'var(--radius)', fontSize:12, color:'var(--text-3)'}}>
                <strong style={{color:'var(--text-2)'}}>Note :</strong> Si l'extraction automatique est incomplète, vous pouvez saisir les opérations manuellement après import.
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
